/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import { get, post } from '@/services/api/client';
import { ApiResponse, PaginatedResponse } from '@/services/api/types';

const BASE_URL = '/admin/migration';

// ── Types ────────────────────────────────────────────────────────────────────

export type MigrationSourceSystem =
  | 'SAGE_100C'
  | 'ODOO'
  | 'QUICKBOOKS_ONLINE'
  | 'QUICKBOOKS_DESKTOP'
  | 'DOLIBARR'
  | 'GENERIC_CSV';

export type MigrationConnectionType =
  | 'SQL_SERVER'
  | 'POSTGRESQL'
  | 'MYSQL'
  | 'REST_API'
  | 'CSV';

export type MigrationJobType = 'BULK' | 'INCREMENTAL';

export type MigrationJobStatus =
  | 'PENDING'
  | 'CONNECTING'
  | 'ANALYZING'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'COMPLETED_WITH_ERRORS'
  | 'FAILED'
  | 'CANCELLED'
  | 'ROLLING_BACK';

export type MigrationEntityType =
  | 'TAX_RATE'
  | 'UOM'
  | 'CATEGORY'
  | 'LOCATION'
  | 'PARTNER'
  | 'PRODUCT'
  | 'STOCK_LEVEL'
  | 'PURCHASE_ORDER'
  | 'SALES_ORDER'
  | 'INVOICE';

export type MigrationAction = 'CREATE' | 'UPDATE' | 'SKIP' | 'FAIL';

export interface MigrationJob {
  id: string;
  orgId: string;
  sourceSystem: MigrationSourceSystem;
  jobType: MigrationJobType;
  connectionType: MigrationConnectionType;
  status: MigrationJobStatus;
  scope: string[];
  totalEntities: number;
  processedEntities: number;
  createdCount: number;
  updatedCount: number;
  skippedCount: number;
  failedCount: number;
  errorMessage: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
}

export interface MigrationProgress {
  status: MigrationJobStatus;
  totalEntities: number;
  processedEntities: number;
  createdCount: number;
  updatedCount: number;
  skippedCount: number;
  failedCount: number;
  percentComplete: number;
}

export interface AnalysisResult {
  entityCounts: Record<MigrationEntityType, number>;
  totalEntities: number;
  estimatedDurationSeconds: number;
}

export interface MigrationEntityLog {
  id: string;
  jobId: string;
  entityType: MigrationEntityType;
  sourceCode: string;
  action: MigrationAction;
  ventaId: string | null;
  errorMessage: string | null;
  createdAt: string;
}

export interface TestConnectionRequest {
  sourceSystem: MigrationSourceSystem;
  connectionType: MigrationConnectionType;
  connectionConfig: Record<string, string>;
}

export interface CreateMigrationJobRequest {
  orgId: string;
  sourceSystem: MigrationSourceSystem;
  jobType: MigrationJobType;
  connectionType: MigrationConnectionType;
  scope: MigrationEntityType[];
  connectionConfig: Record<string, string>;
}

// ── Connection configs per source system ─────────────────────────────────────

export const SOURCE_CONNECTION_TYPES: Record<MigrationSourceSystem, MigrationConnectionType[]> = {
  SAGE_100C: ['SQL_SERVER', 'CSV'],
  ODOO: ['POSTGRESQL', 'REST_API'],
  QUICKBOOKS_ONLINE: ['REST_API'],
  QUICKBOOKS_DESKTOP: ['CSV'],
  DOLIBARR: ['MYSQL'],
  GENERIC_CSV: ['CSV'],
};

export const CONNECTION_FIELDS: Record<MigrationConnectionType, string[]> = {
  SQL_SERVER: ['host', 'port', 'database', 'user', 'password'],
  POSTGRESQL: ['host', 'port', 'database', 'user', 'password'],
  MYSQL: ['host', 'port', 'database', 'user', 'password'],
  REST_API: ['apiUrl', 'apiKey'],
  CSV: [],
};

// ── API Service ──────────────────────────────────────────────────────────────

interface BackendPagedResponse<T> {
  content: T[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
}

export const migrationService = {
  async testConnection(request: TestConnectionRequest): Promise<string> {
    const response = await post<ApiResponse<string>>(
      `${BASE_URL}/test-connection`,
      request
    );
    return response.data;
  },

  async createJob(request: CreateMigrationJobRequest): Promise<MigrationJob> {
    const response = await post<ApiResponse<MigrationJob>>(
      `${BASE_URL}/jobs`,
      request
    );
    return response.data;
  },

  async uploadCsv(jobId: string, entityType: MigrationEntityType, file: File): Promise<string> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await post<ApiResponse<string>>(
      `${BASE_URL}/jobs/${jobId}/upload-csv?entityType=${entityType}`,
      formData
    );
    return response.data;
  },

  async analyzeJob(jobId: string): Promise<AnalysisResult> {
    const response = await post<ApiResponse<AnalysisResult>>(
      `${BASE_URL}/jobs/${jobId}/analyze`
    );
    return response.data;
  },

  async executeJob(jobId: string): Promise<MigrationJob> {
    const response = await post<ApiResponse<MigrationJob>>(
      `${BASE_URL}/jobs/${jobId}/execute`
    );
    return response.data;
  },

  async getJob(jobId: string): Promise<MigrationJob> {
    const response = await get<ApiResponse<MigrationJob>>(
      `${BASE_URL}/jobs/${jobId}`
    );
    return response.data;
  },

  async getProgress(jobId: string): Promise<MigrationProgress> {
    const response = await get<ApiResponse<MigrationProgress>>(
      `${BASE_URL}/jobs/${jobId}/progress`
    );
    return response.data;
  },

  async listJobs(params?: { orgId?: string; page?: number; size?: number }): Promise<PaginatedResponse<MigrationJob>> {
    const response = await get<BackendPagedResponse<MigrationJob>>(
      `${BASE_URL}/jobs`,
      params as Record<string, unknown>
    );
    return {
      content: response.content,
      page: {
        size: response.size,
        number: response.page,
        totalElements: response.totalElements,
        totalPages: response.totalPages,
      },
    };
  },

  async getJobLogs(
    jobId: string,
    params?: { entityType?: string; action?: string; page?: number; size?: number }
  ): Promise<PaginatedResponse<MigrationEntityLog>> {
    const response = await get<BackendPagedResponse<MigrationEntityLog>>(
      `${BASE_URL}/jobs/${jobId}/logs`,
      params as Record<string, unknown>
    );
    return {
      content: response.content,
      page: {
        size: response.size,
        number: response.page,
        totalElements: response.totalElements,
        totalPages: response.totalPages,
      },
    };
  },

  async cancelJob(jobId: string): Promise<MigrationJob> {
    const response = await post<ApiResponse<MigrationJob>>(
      `${BASE_URL}/jobs/${jobId}/cancel`
    );
    return response.data;
  },

  async rollbackJob(jobId: string): Promise<string> {
    const response = await post<ApiResponse<string>>(
      `${BASE_URL}/jobs/${jobId}/rollback`
    );
    return response.data;
  },
};

export default migrationService;
